import { Injectable, inject } from '@angular/core'
import { ApiClient, ApiClientFactory } from '@ftr/api-shared'
import { PrivateConfiguration, UpdateConfigurationBody } from '@ftr/contracts/api/configuration'
import { Uuid } from '@ftr/contracts/type/shared'
import { ApiResult, mapData } from '@ftr/foundation'
import { classToPlain } from '@ftr/serialization'
import { CoreConfigurationService } from '../core-configuration.service'

export interface PersonalizationConfiguration {
  id: Uuid
  countryCode?: string
  stateCode?: string | null
  logoUrl?: string
  ordersHelpEmail?: string
  description?: string
}

@Injectable({
  providedIn: 'root',
})
export class ConfigurePersonalizationService {
  private readonly coreConfigurationService = inject(CoreConfigurationService)

  private readonly apiClient: ApiClient

  constructor() {
    const apiClientFactory = inject(ApiClientFactory)
    this.apiClient = apiClientFactory.build('/configuration')
  }

  getPersonalizationConfiguration(courtSystemId: Uuid): ApiResult<PersonalizationConfiguration> {
    return this.coreConfigurationService
      .getExtendedConfigByCourtSystem(courtSystemId)
      .pipe(mapData(configuration => mapConfigurationToPersonalizationConfiguration(configuration)))
  }

  update(
    configurationId: Uuid,
    countryCode: string,
    stateCode: string | undefined,
    logoUrl: string | undefined,
    ordersHelpEmail: string,
    description?: string,
  ): ApiResult {
    const update = new UpdateConfigurationBody()

    if (countryCode) {
      update.countryCode = countryCode
      update.stateCode = stateCode || null
    }

    update.ordersHelpEmail = ordersHelpEmail
    update.logoUrl = logoUrl || null
    update.description = description

    return this.apiClient.patch({
      path: `/${configurationId}`,
      body: classToPlain(update),
    })
  }
}

function mapConfigurationToPersonalizationConfiguration(
  configuration: PrivateConfiguration,
): PersonalizationConfiguration {
  const { id, countryCode, stateCode, logoUrl, ordersHelpEmail, description } = configuration
  return { id, countryCode, stateCode, logoUrl, ordersHelpEmail, description }
}

export function getDefaultDescription(courtSystemName: string): string {
  return `For more information or assistance, visit the ${courtSystemName} website.`
}
